/**
 * @spec docs/specs/features/staking-position-ui.md#phase-2-transactions
 * Contract addresses, ABIs, and factory functions
 */
import { ethers } from 'ethers';
import { env } from './env';

// Contract addresses
export const CONTRACT_ADDRESSES = {
  BASE: {
    PHNM_TOKEN: env.contracts.phnmTokenAddress,
    PHNM_STAKING: env.contracts.stakingAddress,
    PHNM_CLAIM: env.contracts.claimAddress,
    PHNM_REPOSITORY: env.contracts.repositoryAddress,
    SWAP_RELAYER: env.contracts.swapRelayerAddress,
    USDC_TOKEN: env.contracts.usdcTokenAddress,
    BRIDGE_POOL: env.contracts.bridgePoolAddress,
  },
  POLYGON: {
    USDT_REDEMPTION: env.contracts.usdtRedemptionAddress,
    USDT_TOKEN: env.contracts.polygonUsdtAddress,
    BRIDGE_POOL: env.contracts.polygonBridgePoolAddress,
  },
} as const;

// Minimal ABIs for the functions we need
// Full ABIs can be imported from typechain if needed

/**
 * PHNMHybridStaking ABI - essential functions for transactions
 */
export const STAKING_ABI = [
  // Stake functions
  'function stake(uint256 amount) external returns (uint256 tokenId)',
  'function stakeWithLock(uint256 amount, uint256 lockWeeks) external returns (uint256 tokenId)',

  // Unstake functions
  'function initiateUnstake(uint256 tokenId) external',
  'function initiatePartialUnstake(uint256 tokenId, uint256 amount) external',
  'function cancelCooldown(uint256 tokenId) external',
  'function withdraw(uint256 tokenId) external',

  // Position management
  'function merge(uint256 tokenId1, uint256 tokenId2) external returns (uint256 newTokenId)',
  'function convertToLock(uint256 tokenId, uint256 lockWeeks) external',

  // View functions
  'function getPosition(uint256 tokenId) external view returns (tuple(uint128 amount, uint64 startTime, uint64 lockEndTime, uint64 cooldownStartTime, uint8 status))',
  'function positionCount(address user) external view returns (uint256)',
  'function balanceOf(address owner) external view returns (uint256)',
  'function totalStaked() external view returns (uint256)',
  'function ownerOf(uint256 tokenId) external view returns (address)',
  'function treasury() external view returns (address)',
  'function phnmToken() external view returns (address)',
  'function paused() external view returns (bool)',
  'function totalSupply() external view returns (uint256)', // ERC721 - total position NFTs

  // Constants
  'function MIN_STAKE() external view returns (uint256)',
  'function MAX_POSITIONS() external view returns (uint256)',
  'function COOLDOWN_PERIOD() external view returns (uint256)',
  'function MIN_LOCK_WEEKS() external view returns (uint256)',
  'function MAX_LOCK_WEEKS() external view returns (uint256)',

  // Events
  'event Staked(address indexed user, uint256 indexed tokenId, uint256 amount)',
  'event StakedWithLock(address indexed user, uint256 indexed tokenId, uint256 amount, uint256 lockWeeks)',
  'event UnstakeInitiated(address indexed user, uint256 indexed tokenId, uint256 cooldownEnd)',
  'event CooldownCancelled(address indexed user, uint256 indexed tokenId)',
  'event Withdrawn(address indexed user, uint256 indexed tokenId, uint256 amount)',
  'event PositionsMerged(address indexed user, uint256 indexed tokenId1, uint256 indexed tokenId2, uint256 newTokenId)',
] as const;

/**
 * ERC20 ABI - for PHNM token approval and supply queries
 */
export const ERC20_ABI = [
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function transfer(address to, uint256 amount) external returns (bool)',
  'function transferFrom(address from, address to, uint256 amount) external returns (bool)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function balanceOf(address account) external view returns (uint256)',
  'function totalSupply() external view returns (uint256)',
  'function decimals() external view returns (uint8)',
  'function symbol() external view returns (string)',
  'function nonces(address owner) external view returns (uint256)',
] as const;

/**
 * PHNM Claim contract ABI - for gasless claims
 */
export const CLAIM_ABI = [
  'function claim(address recipient, uint256 amount, bytes32[] calldata proof) external',
  'function claimed(address account) external view returns (bool)',
  'function claimableAmount(address account, uint256 amount, bytes32[] calldata proof) external view returns (uint256)',
  'event Claimed(address indexed recipient, uint256 amount)',
] as const;

/**
 * USDT Redemption contract ABI - for USDT claims on Polygon
 */
export const REDEMPTION_ABI = [
  'function redeem(uint256 amount, bytes32[] calldata proof) external',
  'function redeemed(address account) external view returns (bool)',
  'function redeemableAmount(address account, uint256 amount, bytes32[] calldata proof) external view returns (uint256)',
  'event Redeemed(address indexed account, uint256 amount)',
] as const;

/**
 * SwapRelayer contract ABI - for gasless PHNM/USDC swaps
 */
export const SWAP_RELAYER_ABI = [
  'function executeSwap(tuple(address from, address to, uint256 value, uint256 validAfter, uint256 validBefore, bytes32 nonce) receiveAuth, bytes receiveAuthSignature, tuple(address user, address tokenIn, address tokenOut, uint256 amountIn, uint256 amountOutMin, uint256 deadline, bytes32 transferNonce) swapAuth, bytes swapAuthSignature) external',
  'function usedNonces(bytes32 nonce) external view returns (bool)',
  'function phnmToken() external view returns (address)',
  'function usdcToken() external view returns (address)',
  'function router() external view returns (address)',
  'event SwapExecuted(address indexed user, address indexed tokenIn, address indexed tokenOut, uint256 amountIn, uint256 amountOut)',
] as const;

/**
 * EIP-712 type definitions for SwapRelayer
 */
export const SWAP_AUTHORIZATION_TYPES = {
  SwapAuthorization: [
    { name: 'user', type: 'address' },
    { name: 'tokenIn', type: 'address' },
    { name: 'tokenOut', type: 'address' },
    { name: 'amountIn', type: 'uint256' },
    { name: 'amountOutMin', type: 'uint256' },
    { name: 'deadline', type: 'uint256' },
    { name: 'transferNonce', type: 'bytes32' },
  ],
} as const;

/**
 * EIP-3009 ReceiveWithAuthorization type definitions
 */
export const RECEIVE_AUTHORIZATION_TYPES = {
  ReceiveWithAuthorization: [
    { name: 'from', type: 'address' },
    { name: 'to', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'validAfter', type: 'uint256' },
    { name: 'validBefore', type: 'uint256' },
    { name: 'nonce', type: 'bytes32' },
  ],
} as const;

/**
 * EIP-3009 TransferWithAuthorization type definitions (for gasless ERC-20 sends)
 */
export const TRANSFER_AUTHORIZATION_TYPES = {
  TransferWithAuthorization: [
    { name: 'from', type: 'address' },
    { name: 'to', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'validAfter', type: 'uint256' },
    { name: 'validBefore', type: 'uint256' },
    { name: 'nonce', type: 'bytes32' },
  ],
} as const;

/**
 * EIP-2612 Permit type definitions (for USDT gasless sends via PermitForwarder)
 */
export const PERMIT_TYPES_EIP2612 = {
  Permit: [
    { name: 'owner', type: 'address' },
    { name: 'spender', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint256' },
  ],
} as const;

/**
 * TransferNFT type definitions (for gasless NFT transfers via MetaTxFacet)
 */
export const TRANSFER_NFT_TYPES = {
  TransferNFT: [
    { name: 'owner', type: 'address' },
    { name: 'to', type: 'address' },
    { name: 'tokenId', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint256' },
  ],
} as const;

/**
 * Create a staking contract instance
 */
export function getStakingContract(
  signerOrProvider: ethers.Signer | ethers.Provider
): ethers.Contract {
  const address = CONTRACT_ADDRESSES.BASE.PHNM_STAKING;
  if (!address) {
    throw new Error('Staking contract address not configured');
  }
  return new ethers.Contract(address, STAKING_ABI, signerOrProvider);
}

/**
 * Create an ERC20 contract instance (for PHNM token)
 */
export function getTokenContract(
  tokenAddress: string,
  signerOrProvider: ethers.Signer | ethers.Provider
): ethers.Contract {
  return new ethers.Contract(tokenAddress, ERC20_ABI, signerOrProvider);
}

/**
 * Create a claim contract instance
 */
export function getClaimContract(
  signerOrProvider: ethers.Signer | ethers.Provider
): ethers.Contract {
  const address = CONTRACT_ADDRESSES.BASE.PHNM_CLAIM;
  if (!address) {
    throw new Error('Claim contract address not configured');
  }
  return new ethers.Contract(address, CLAIM_ABI, signerOrProvider);
}

/**
 * Create a USDT redemption contract instance
 */
export function getRedemptionContract(
  signerOrProvider: ethers.Signer | ethers.Provider
): ethers.Contract {
  const address = CONTRACT_ADDRESSES.POLYGON.USDT_REDEMPTION;
  if (!address) {
    throw new Error('Redemption contract address not configured');
  }
  return new ethers.Contract(address, REDEMPTION_ABI, signerOrProvider);
}

/**
 * Get PHNM token address from staking contract
 * Note: This is a view call, so it doesn't need a signer
 */
export async function getPHNMTokenAddress(provider: ethers.Provider): Promise<string> {
  // The staking contract has a phnmToken() view function
  const extendedAbi = [...STAKING_ABI, 'function phnmToken() external view returns (address)'];
  const contract = new ethers.Contract(
    CONTRACT_ADDRESSES.BASE.PHNM_STAKING!,
    extendedAbi,
    provider
  );
  return await contract.phnmToken();
}

/**
 * Create a SwapRelayer contract instance
 */
export function getSwapRelayerContract(
  signerOrProvider: ethers.Signer | ethers.Provider
): ethers.Contract {
  const address = CONTRACT_ADDRESSES.BASE.SWAP_RELAYER;
  if (!address) {
    throw new Error('SwapRelayer contract address not configured');
  }
  return new ethers.Contract(address, SWAP_RELAYER_ABI, signerOrProvider);
}

/**
 * EIP-2612 Permit type definitions for Polygon USDT0.
 * Polygon USDT uses a salt-based EIP-712 domain (no chainId field).
 * Verified on-chain: name="USDT0", version="1", salt=bytes32(137).
 */
export const PERMIT_TYPES = {
  Permit: [
    { name: 'owner', type: 'address' },
    { name: 'spender', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint256' },
  ],
} as const;
